// Portfolio Administration v1.0 PR 4a §7.5 — File UCC-3 Amendment modal.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useFileUccAmendment } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface FileAmendmentModalProps {
  loanId: string;
  parent: UccFilingDto;
  open: boolean;
  onClose: () => void;
}

export function FileAmendmentModal({
  loanId,
  parent,
  open,
  onClose,
}: FileAmendmentModalProps) {
  const toast = useToast();
  const fileAmendment = useFileUccAmendment(loanId);

  const tz = useUserTimeZone();
  const today = isoDateInTz(new Date(), tz);
  const [filingDate, setFilingDate] = useState(today);
  const [filingNumber, setFilingNumber] = useState("");
  const [filingOffice, setFilingOffice] = useState(parent.filingOffice);
  const [filingJurisdiction, setFilingJurisdiction] = useState(parent.filingJurisdiction);
  const [body, setBody] = useState("");

  const onSubmit = async () => {
    if (!filingNumber.trim()) {
      toast.error("Filing number is required.");
      return;
    }
    try {
      await fileAmendment.mutateAsync({
        parentFilingId: parent.id,
        loanId,
        filingDate,
        filingNumber,
        filingOffice,
        filingJurisdiction,
        amendmentBody: body || null,
      });
      toast.success("Amendment filed.");
      setFilingNumber("");
      setBody("");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not file amendment.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="File UCC-3 Amendment"
      description={`Parent: ${parent.filingNumber}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={fileAmendment.isPending || !filingNumber.trim()}
            data-testid="ucc-amendment-submit"
          >
            File amendment
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="ucc-amendment-form">
        <div className="grid grid-cols-2 gap-space-2">
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">Filing date</span>
            <input
              type="date"
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={filingDate}
              onChange={(e) => setFilingDate(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">Filing number</span>
            <input
              className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
              value={filingNumber}
              onChange={(e) => setFilingNumber(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">Filing office</span>
            <input
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={filingOffice}
              onChange={(e) => setFilingOffice(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">Jurisdiction</span>
            <input
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={filingJurisdiction}
              onChange={(e) => setFilingJurisdiction(e.target.value)}
            />
          </label>
        </div>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Amendment body
          </span>
          <Textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Description of what is being amended (collateral, parties, etc.)"
          />
        </label>
      </div>
    </Modal>
  );
}
